import type { HttpClient } from './httpClient';
import type {
  StreamlakeFeatureProperty,
  StreamlakeIdentityField,
  StreamlakeOverviewResponse,
  StreamlakeProfileDetailResponse,
  StreamlakeProfileFilter,
  StreamlakeUpsOverviewResponse,
  ListStreamlakeFeaturePropertiesResponse,
  ListStreamlakeIdentityFieldsResponse,
  ListStreamlakeSourcePipelinesResponse,
  StreamlakeOnlineStoreResponse,
  SearchStreamlakeProfilesResponse,
  UpdateStreamlakeOnlineStoreRequest,
} from './streamlakeTypes';

interface SwrResult<T> {
  body: T | undefined;
  isLoading: boolean;
  error: unknown;
  mutate: () => Promise<unknown>;
}

// The Data Engine domain has no generated SDK yet, so every call goes
// through the raw httpClient against hand-built URLs (same ship-ahead-of-
// codegen pattern as gdsg.ts). Swap to generated Api classes once the domain
// lands in @repo/api-gw-sdk.
export const streamlake = (
  httpClient: HttpClient,
  currentProjectId: string
) => {
  const root = `/projects/${currentProjectId}/streamlake`;
  const get = <T>(url: string | null) =>
    httpClient.get(url, { shouldRetryOnError: false }) as SwrResult<T>;

  return {
    overview: {
      useGet: () => get<StreamlakeOverviewResponse>(`${root}/overview`),
    },
    // Sampling policies have no methods here. A policy is a property of its
    // pipeline (`nativeStreamProperties.sampling`), so reading one is reading the
    // pipeline, changing its mode is `dal.lakehouse.patchIngestionPipeline`, and
    // estimating its impact is `dal.lakehouse.simulateIngestionPipelineSampling`
    // — all endpoints a deployment actually serves. The StreamLake-shaped
    // `sampling-policies` collection was mock-only: it 404'd against the real
    // gateway, and its `PATCH {mode}` faked lifecycle transitions the backend has
    // no concept of.
    ups: {
      useOverview: () =>
        get<StreamlakeUpsOverviewResponse>(`${root}/ups/overview`),
      sourcePipelines: {
        useList: () =>
          get<ListStreamlakeSourcePipelinesResponse>(
            `${root}/ups/source-pipelines`
          ),
        setAttached: (
          pipelineId: string,
          attached: boolean
        ): Promise<{ pipelineId: string; attached: boolean }> =>
          httpClient.patch(`${root}/ups/source-pipelines/${pipelineId}`, {
            attached,
          }),
      },
      identityFields: {
        useList: () =>
          get<ListStreamlakeIdentityFieldsResponse>(
            `${root}/ups/identity-fields`
          ),
        replace: (
          fields: StreamlakeIdentityField[]
        ): Promise<{ fields: StreamlakeIdentityField[] }> =>
          httpClient.put(`${root}/ups/identity-fields`, { fields }),
      },
      features: {
        useList: () =>
          get<ListStreamlakeFeaturePropertiesResponse>(`${root}/ups/features`),
        create: (
          body: Omit<StreamlakeFeatureProperty, 'id' | 'comments'>
        ): Promise<StreamlakeFeatureProperty> =>
          httpClient.post(`${root}/ups/features`, body),
        update: (
          featureId: string,
          body: Partial<Omit<StreamlakeFeatureProperty, 'id' | 'comments'>>
        ): Promise<StreamlakeFeatureProperty> =>
          httpClient.patch(`${root}/ups/features/${featureId}`, body),
        remove: (featureId: string): Promise<{ id: string }> =>
          httpClient.delete(`${root}/ups/features/${featureId}`),
        addComment: (
          featureId: string,
          text: string
        ): Promise<StreamlakeFeatureProperty> =>
          httpClient.post(`${root}/ups/features/${featureId}/comments`, {
            text,
          }),
      },
      onlineStore: {
        useGet: () =>
          get<StreamlakeOnlineStoreResponse>(`${root}/ups/online-store`),
        update: (
          body: UpdateStreamlakeOnlineStoreRequest
        ): Promise<StreamlakeOnlineStoreResponse> =>
          httpClient.put(`${root}/ups/online-store`, body),
      },
      profiles: {
        search: (
          filters: StreamlakeProfileFilter[]
        ): Promise<SearchStreamlakeProfilesResponse> =>
          httpClient.post(`${root}/ups/profiles/search`, { filters }),
        useGet: (profileId: string | undefined) =>
          get<StreamlakeProfileDetailResponse>(
            profileId
              ? `${root}/ups/profiles/${encodeURIComponent(profileId)}`
              : null
          ),
      },
    },
    // The catalog tree, per-table reads, and the query runner used to live
    // here as StreamLake-only endpoints with no deployed counterpart — the
    // calls 404'd against a real gateway. They are gone: the catalog screen
    // browses the real catalog through `dal.lakehouse` (`listDataCatalogs` +
    // `accessDataCatalog` for namespaces/tables/schema, `getTableSnapshots` /
    // `getTableSample` for history and row previews), and the query editor is
    // the Lakehouse console's editor against the real sql-engine endpoints, so
    // mock mode and a real workspace exercise one implementation instead of two.
  };
};
